import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import '../../assets/css/comments.css';

const fetchComments = async (postId, postType) => {
    const res = await fetch(`/api/comments/post/${postId}/type/${postType}`);
    if (!res.ok) throw new Error('Fehler beim Laden der Kommentare');
    return await res.json();
};

const postComment = async (postId, content, postType, userId, token) => {
    const res = await fetch(`/api/comments`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
            post_id: postId,
            post_type: postType,
            user_id: userId,
            content,
        }),
    });

    if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Fehler beim Absenden des Kommentars');
    }

    return await res.json(); // z. B. { insertId: 123 }
};

const CommentSection = ({ postId, postType }) => {  // postType als Prop hinzufügen
    const { user, token } = useAuth();
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const loadComments = async () => {
            try {
                const data = await fetchComments(postId, postType);  // postType hier übergeben
                setComments(data);
            } catch (err) {
                setError(err.message || 'Fehler beim Laden der Kommentare.');
            } finally {
                setLoading(false);
            }
        };
        loadComments();
    }, [postId, postType]);  // postType ist jetzt eine Abhängigkeit

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newComment.trim()) return;

        try {
            const res = await postComment(postId, newComment, postType, user.id, token);  // postType hier verwenden

            const newCommentObj = {
                id: res.insertId || Date.now(),
                user: user.username,
                user_role: user.role_name || 'User',
                user_image: user.user_image || 'https://placehold.co/100x100?text=User',
                content: newComment,
                date: new Date().toISOString(),
            };

            setComments((prev) => [...prev, newCommentObj]);
            setNewComment('');
        } catch (err) {
            setError(err.message || 'Fehler beim Absenden des Kommentars.');
        }
    };

    const formatDateTime = (isoString) => {
        const date = new Date(isoString);
        return date.toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }) + ', ' + date.toLocaleTimeString('de-DE', {
            hour: '2-digit',
            minute: '2-digit',
        }) + ' Uhr';
    };

    return (
        <div className="comments-section">
            <h2>Kommentare</h2>

            {user ? (
                <form onSubmit={handleSubmit} className="form">
                    <textarea
                        rows={3}
                        placeholder="Nimm an der Diskussion teil..."
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                    />
                    <div>
                        <button className="btn btn-primary" type="submit">
                            Kommentar absenden
                        </button>
                    </div>
                </form>
            ) : (
                <div className="login-info">
                    <p>Nur angemeldete Benutzer können kommentieren und bewerten.</p>
                    <div className="btn-group">
                        <Link to="/login" className="btn btn-primary">Ich habe ein Konto</Link>
                        <Link to="/register" className="btn btn-secondary">Kostenlos registrieren</Link>
                    </div>
                </div>
            )}

            {loading ? (
                <p>Lade Kommentare...</p>
            ) : comments.length === 0 ? (
                <p>Bisher sind keine Kommentare vorhanden.</p>
            ) : (
                <ul className="comments">
                    {comments.map(comment => (
                        <li key={comment.id} className="comment">
                            <div className="user-info">
                                <img
                                    className="user-image"
                                    src={comment.user_image || 'https://placehold.co/100x100?text=User'}
                                    alt={comment.user}
                                />
                                <div className="user-and-time">
                                    <div className="user">
                                        {comment.user}
                                        {(comment.user_role === "Abonnent" || comment.user_role === "Moderator" || comment.user_role === "Redakteur" || comment.user_role === "Admin") && (
                                            <div className="user-badge">{comment.user_role}</div>
                                        )}
                                    </div>
                                    <div className="time">{formatDateTime(comment.date)}</div>
                                </div>
                            </div>
                            <p>{comment.content}</p>
                        </li>
                    ))}
                </ul>
            )}

            {error && <p className="text-red-500 mt-2">{error}</p>}
        </div>
    );
};

export default CommentSection;
